import React, { useEffect, useState } from 'react';
import { timeLogsAPI, tasksAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, PageHeader, Button, Modal, FormField, Table, Empty, Alert, StatCard, FilterPills } from '../components/ui';
import { Plus, Pencil, Trash2, Clock } from 'lucide-react';

// Backend TimeLog model fields:
//   log_id | user_id (auto from JWT) | task_id | hours_spent | date | notes
// FIXES: hours→hours_spent, timelog_id→log_id, description→notes

const emptyForm = {
  task_id: '',
  hours_spent: '',   // NOT "hours"
  notes: '',         // NOT "description"
  date: new Date().toISOString().split('T')[0],
};

export default function TimeLogs() {
  const { canCreate, canUpdate, canDelete } = useAuth();
  const [logs, setLogs] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const load = () => {
    setLoading(true);
    Promise.all([timeLogsAPI.list(), tasksAPI.list()])
      .then(([l, t]) => { setLogs(l.data.data || []); setTasks(t.data.data || []); })
      .catch(err => setError(err.response?.data?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setError(''); setModal(true); };
  const openEdit = (log) => {
    setEditing(log);
    setForm({
      task_id:     log.task_id,
      hours_spent: log.hours_spent,  // correct field
      notes:       log.notes || '',   // correct field
      date:        log.date || '',
    });
    setError(''); setModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      if (editing) {
        // PUT only allows: hours_spent, date, notes (task_id cannot change)
        await timeLogsAPI.update(editing.log_id, {  // correct id field
          hours_spent: parseFloat(form.hours_spent),
          notes: form.notes,
          date: form.date,
        });
      } else {
        await timeLogsAPI.create({
          task_id:     form.task_id,
          hours_spent: parseFloat(form.hours_spent),
          notes:       form.notes,
          date:        form.date,
        });
      }
      setModal(false); load();
    } catch (err) { setError(err.response?.data?.message || 'Error saving time log'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this time log?')) return;
    try { await timeLogsAPI.delete(id); load(); }
    catch (err) { alert(err.response?.data?.message || 'Error deleting'); }
  };

  const totalHours = logs.reduce((s, l) => s + parseFloat(l.hours_spent || 0), 0);
  const filteredLogs = search
    ? logs.filter(l => (l.task?.task_name || '').toLowerCase().includes(search.toLowerCase()) || (l.user?.name || '').toLowerCase().includes(search.toLowerCase()) || (l.notes || '').toLowerCase().includes(search.toLowerCase()))
    : logs;

  const columns = [
    { key: 'date',        label: 'Date',  render: r => r.date || new Date(r.createdAt).toLocaleDateString() },
    { key: 'task',        label: 'Task',  render: r => r.task?.task_name || `Task #${r.task_id}` },
    { key: 'user',        label: 'User',  render: r => r.user?.name || '—' },
    { key: 'hours_spent', label: 'Hours', render: r => (
      <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--accent)' }}>
        {parseFloat(r.hours_spent || 0).toFixed(1)}h
      </span>
    )},
    { key: 'notes', label: 'Notes', render: r => <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>{r.notes || '—'}</span> },
    { key: 'actions', label: '', render: r => (canUpdate || canDelete) ? (
      <div style={{ display: 'flex', gap: 8 }}>
        {canUpdate && <Button size="sm" variant="ghost" onClick={e => { e.stopPropagation(); openEdit(r); }}><Pencil size={13} /></Button>}
        {canDelete && <Button size="sm" variant="danger" onClick={e => { e.stopPropagation(); handleDelete(r.log_id); }}><Trash2 size={13} /></Button>}
      </div>
    ) : null},
  ];

  return (
    <div className="animate-in">
      <PageHeader title="Time Logs" subtitle={`${filteredLogs.length} entries · ${totalHours.toFixed(1)}h total`} eyebrow="Tracking"
        action={canCreate ? <Button onClick={openCreate}><Plus size={15} />Log Time</Button> : null} />

      <div style={{ marginBottom: 16 }}>
        <input
          placeholder="Search by task, user or notes..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width: '100%', maxWidth: 340, padding: '7px 14px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text)', fontSize: 13 }}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px,1fr))', gap: 16, marginBottom: 24 }}>
        <StatCard label="Total Hours"   value={`${totalHours.toFixed(1)}h`} icon={Clock} color="var(--accent)" />
        <StatCard label="Log Entries"   value={logs.length}                  icon={Clock} color="#60a5fa" />
        <StatCard label="Contributors"  value={new Set(logs.map(l => l.user_id)).size} icon={Clock} color="#34d399" />
      </div>

      <Card>
        {loading ? <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</div>
          : filteredLogs.length === 0 ? <Empty message="No time logs found" />
          : <Table columns={columns} data={filteredLogs.map(l => ({ ...l, id: l.log_id }))} />}
      </Card>

      <Modal open={modal} onClose={() => setModal(false)} title={editing ? 'Edit Time Log' : 'Log Time'}>
        <Alert type="error" message={error} />
        <form onSubmit={handleSave}>
          <FormField label="Task">
            <select value={form.task_id} onChange={e => setForm(f => ({...f, task_id: e.target.value}))} required disabled={!!editing}>
              <option value="">Select task...</option>
              {tasks.map(t => <option key={t.task_id} value={t.task_id}>{t.task_name}</option>)}
            </select>
            {editing && <p style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 4 }}>Task cannot be changed when editing.</p>}
          </FormField>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <FormField label="Hours Spent">
              <input type="number" value={form.hours_spent} onChange={e => setForm(f => ({...f, hours_spent: e.target.value}))} required min="0.1" max="24" step="0.1" placeholder="e.g. 2.5" />
            </FormField>
            <FormField label="Date">
              <input type="date" value={form.date} onChange={e => setForm(f => ({...f, date: e.target.value}))} required />
            </FormField>
          </div>
          <FormField label="Notes">
            <textarea rows={3} value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} placeholder="What did you work on?" style={{ resize: 'vertical' }} />
          </FormField>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
            <Button variant="secondary" onClick={() => setModal(false)} type="button">Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Saving...' : editing ? 'Update' : 'Log Time'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
